import { useEffect, useMemo, useRef, useState } from 'react';

/** Campo de texto que também é lista: ao focar, mostra o que já existe; ao digitar, filtra;
 *  e o que for digitado e não existir vale como valor novo (o backend cadastra na hora).
 *
 *  É o mesmo gesto para "escolher o Tiny que já existe" e para "cadastrar o primeiro" — que é
 *  justamente o que evita "TINY" e "Tiny" convivendo no banco.
 */
export default function ComboBox({ valor, opcoes = [], onChange, placeholder, id }) {
  const [aberto, setAberto] = useState(false);
  const [destaque, setDestaque] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const fechar = (e) => {
      if (!containerRef.current?.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [aberto]);

  const filtradas = useMemo(() => {
    const termo = (valor || '').trim().toLowerCase();
    if (!termo) return opcoes;
    return opcoes.filter((o) => o.toLowerCase().includes(termo));
  }, [valor, opcoes]);

  // só é "novo" quando não existe nenhuma opção igual — comparação sem diferenciar
  // maiúsculas, igual à do backend, para a tela não prometer um cadastro que não vai acontecer
  const ehNovo = Boolean((valor || '').trim()) && !opcoes.some((o) => o.toLowerCase() === valor.trim().toLowerCase());

  const escolher = (opcao) => {
    onChange(opcao);
    setAberto(false);
  };

  const aoTeclar = (e) => {
    if (e.key === 'Escape') {
      setAberto(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAberto(true);
      setDestaque((d) => Math.min(d + 1, filtradas.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestaque((d) => Math.max(d - 1, 0));
    } else if (e.key === 'Enter' && aberto && filtradas[destaque]) {
      e.preventDefault();
      escolher(filtradas[destaque]);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div className="d-flex align-items-center" style={{ position: 'relative' }}>
        <input
          id={id}
          className="wh-input"
          value={valor || ''}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setAberto(true)}
          onChange={(e) => {
            onChange(e.target.value);
            setAberto(true);
            setDestaque(0);
          }}
          onKeyDown={aoTeclar}
        />
        <i
          className={`bi bi-chevron-${aberto ? 'up' : 'down'}`}
          style={{ position: 'absolute', right: 10, color: 'var(--wh-muted)', pointerEvents: 'none', fontSize: 12 }}
        />
      </div>

      {aberto && (
        <div
          className="card-soft"
          style={{ position: 'absolute', zIndex: 20, width: '100%', marginTop: 4, maxHeight: 220, overflowY: 'auto' }}
        >
          {filtradas.length === 0 && !ehNovo && (
            <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--wh-muted)' }}>
              Nenhum cadastro ainda — digite para criar o primeiro.
            </div>
          )}
          {filtradas.map((opcao, i) => (
            <button
              key={opcao}
              type="button"
              className="w-100 text-start"
              style={{
                border: 'none',
                background: i === destaque ? 'var(--wh-accent-soft)' : 'transparent',
                padding: '8px 12px',
                fontSize: 13,
                cursor: 'pointer',
              }}
              onMouseEnter={() => setDestaque(i)}
              onClick={() => escolher(opcao)}
            >
              {opcao}
            </button>
          ))}
          {ehNovo && (
            <div
              style={{
                padding: '8px 12px',
                fontSize: 12.5,
                color: 'var(--wh-sucesso)',
                borderTop: filtradas.length ? '1px solid var(--wh-border-2)' : 'none',
              }}
            >
              <i className="bi bi-plus-circle" /> "{valor.trim()}" será cadastrado ao salvar
            </div>
          )}
        </div>
      )}
    </div>
  );
}
